// Two Pointers — full course content for the DSA Explanation Modal.
// Java-focused. Same section-course schema as arrays.js/hashing.js: concept → internals →
// the pattern's sub-variants → errors → complexity → beginner drills → cheatsheet → playground.

import twoPointersDrills from './twoPointersDrills';
const drill = id => twoPointersDrills.find(d => d.id === id);

const twoPointers = {
  key: 'Two Pointers',
  tagline: 'Two positions moving through the same data, in step, so the answer falls out in one pass instead of two nested loops.',

  sections: [
    {
      id: 'intro',
      title: 'Introduction to Two Pointers',
      type: 'text',
      body: [
        "A two-pointer approach solves a problem by tracking two indices into the same array (or string) at once — usually named left and right, or slow and fast — and moving them according to a rule instead of comparing every element to every other element.",
        "It isn't one algorithm; it's a family of movement rules. Sometimes the pointers start at opposite ends and walk toward each other. Sometimes they both start at the beginning and one simply moves faster than the other. What ties every variant together is the same idea: instead of nested loops re-examining the same data, a single coordinated pass does the whole job.",
      ],
    },
    {
      id: 'why',
      title: 'Why Two Pointers?',
      type: 'text',
      body: [
        "Two very different kinds of waste show up constantly in array problems, and two pointers fix both. The first is extra memory: reversing an array by building a brand-new reversed copy, or filtering duplicates into a second list, costs O(n) space for something that could happen in the original array. The second is extra time: checking every pair of elements for a target sum costs O(n²), even when the array is sorted and 90% of those comparisons are pointless.",
        "Two pointers fix the memory problem by swapping values in place instead of copying them somewhere else, and fix the time problem by using structure in the data (sortedness, a known midpoint, a boundary condition) to decide which single direction to move next — collapsing what looked like a nested loop into one coordinated sweep.",
        "The pattern comes in three recognizable shapes, covered one at a time later in this course: pointers converging from opposite ends, pointers moving in the same direction at different speeds (one marking a write position, one scanning ahead), and the fast/slow variant used to find a midpoint or detect a cycle.",
      ],
    },
    {
      id: 'analogy',
      title: 'How Two Pointers Works — Two Real-World Pictures',
      type: 'text',
      heading: 'Folding a Bedsheet, and the Tortoise on a Straight Track',
      body: [
        "Picture two people folding a large bedsheet. Each one grabs a corner — one at the top-left, one at the bottom-right — and they walk toward each other, bringing corner to corner. They never need a third person standing in the middle checking every corner against every other corner; they just need to know where their own hand currently is, and to keep walking inward until they meet. That's exactly what opposite-direction two pointers do to a palindrome check, a reversal, or a sorted-array pair search: left starts at one end, right starts at the other, and each step brings them one position closer to the middle. If the two \"corners\" don't match (a palindrome mismatch) or the sum needs adjusting (too big → pull right in; too small → push left forward), you know immediately which single hand needs to move, without ever re-checking a corner you've already folded.",
        "Now picture two joggers on a straight running track, not a loop: one jogs at normal pace (the slow pointer), the other jogs at exactly double speed (the fast pointer). Nobody needs to check the track's total length beforehand — by the time the fast jogger reaches the finish line, the slow jogger is standing exactly at the halfway point, purely because \"twice as fast\" means \"covers twice the distance\" in the same time. This is formally known as the tortoise-and-hare technique, and its real payoff shows up on a looping track (a linked list with a cycle): if the fast jogger ever laps the slow one and they end up standing in the same spot again, you've proven the track loops back on itself — without ever needing to count how long the loop is.",
      ],
    },
    {
      id: 'howworks',
      title: 'Visualizing Pointer Movement',
      type: 'pointer-diagram',
      body: [
        "Every two-pointer technique boils down to: where do left and right start, and what rule decides which one moves? The diagram below freezes one moment mid-algorithm — a sorted-array pair search converging toward the middle — so you can see both pointers sitting on the array at once, exactly like the sidebar of a debugger.",
        "In this snapshot, left sits early in the array and right sits later. Their combined value is compared to a target, and whichever direction the comparison points, exactly one of the two pointers moves — never both, and never a third index.",
      ],
      demo: {
        values: [2, 7, 11, 15, 20],
        pointers: [
          { label: 'left', index: 1, color: '#7F00FF' },
          { label: 'right', index: 3, color: '#3B82F6' },
        ],
        note: 'arr[left] + arr[right] = 7 + 15 = 22. If target were 22, this is a match; otherwise exactly one pointer moves inward.',
      },
    },
    {
      id: 'patterns',
      title: 'The Three Two-Pointer Patterns',
      type: 'text',
      body: [
        "Opposite-direction (converging): left starts at index 0, right starts at the last index, and they move toward each other. Used for reversal, palindrome checks, and finding a pair with a given sum in a sorted array. Requires the data to have some usable order (sorted, or symmetric like a string) so that moving a pointer inward is a meaningful, safe decision.",
        "Same-direction (read/write, sometimes called slow/fast): both pointers start at the beginning, but one (often called slow, or a write pointer) marks where the next \"kept\" value goes, while the other (fast, or a read pointer) scans ahead looking for the next value worth keeping. Used for removing duplicates in place and partitioning values (evens before odds, for example).",
        "Fast & slow (different speeds): both pointers start together, but fast advances twice as fast as slow. Used to find the middle of a sequence without knowing its length upfront, and — on a linked list rather than an array — to detect whether the sequence loops back on itself (a cycle).",
      ],
    },
    {
      id: 'declare',
      title: 'Setting Up Two Pointers in Java',
      type: 'code',
      code:
`// Opposite-direction: start at both ends
int left = 0, right = arr.length - 1;

// Same-direction: both start together, "slow" marks the write position
int slow = 0;
for (int fast = 1; fast < arr.length; fast++) { /* ... */ }

// Fast & slow: both start together, fast moves twice per step
int slowPtr = 0, fastPtr = 0;`,
      note: 'No special Java syntax is needed — two pointers are just two int variables you update deliberately. The pattern lives entirely in the movement rule, not in any language feature.',
    },
    {
      id: 'opposite',
      title: 'Opposite-Direction Pattern (Converging)',
      type: 'code',
      code:
`int left = 0, right = arr.length - 1;
while (left < right) {
    int sum = arr[left] + arr[right];
    if (sum == target) {
        // found it — use left and right
        break;
    } else if (sum < target) {
        left++;   // need a bigger sum — the only lever that increases it
    } else {
        right--;  // need a smaller sum — the only lever that decreases it
    }
}`,
      note: 'This only works because the array is sorted: increasing left strictly increases the sum, decreasing right strictly decreases it. Without sortedness, moving a pointer wouldn\'t predictably move the sum in one direction.',
    },
    {
      id: 'samedirection',
      title: 'Same-Direction Pattern (Read/Write Pointers)',
      type: 'code',
      code:
`int slow = 0; // slow marks the last position of the "kept" prefix
for (int fast = 1; fast < arr.length; fast++) {
    if (arr[fast] != arr[slow]) {   // condition for "this value is worth keeping"
        slow++;
        arr[slow] = arr[fast];
    }
}
// arr[0..slow] now holds the compacted result`,
      note: 'fast explores every element exactly once; slow only advances when something is actually worth writing. The array rearranges itself in place — no second array, no extra list.',
    },
    {
      id: 'fastslow',
      title: 'Fast & Slow Pointers (Finding a Midpoint / Detecting a Cycle)',
      type: 'code',
      code:
`// Find the middle without knowing the length upfront
int slow = 0, fast = 0;
while (fast < arr.length - 1) {
    slow++;
    fast += 2;
}
// slow is now the middle index

// The same idea on a linked list detects a cycle (Floyd's algorithm)
ListNode slowNode = head, fastNode = head;
while (fastNode != null && fastNode.next != null) {
    slowNode = slowNode.next;
    fastNode = fastNode.next.next;
    if (slowNode == fastNode) {
        // a cycle exists — fast lapped slow
        break;
    }
}`,
      note: 'On an array, this is a novelty — you could just use length / 2. On a linked list, where there\'s no length field to read, this is the only O(1)-space way to find the middle or prove a cycle exists.',
    },
    {
      id: 'errors',
      title: 'Common Errors',
      type: 'errors',
      items: [
        {
          mistake: 'Using two pointers on unsorted data expecting sorted-array behavior',
          why: 'The opposite-direction pair-sum technique relies on "moving left increases the sum, moving right decreases it" — a guarantee that only holds when the array is sorted. On unsorted data, that guarantee is gone and the technique silently gives wrong answers.',
          fix: 'Sort the array first if it isn\'t already sorted (and the problem allows it), or recognize that this specific variant doesn\'t apply and a hashing approach (like Two Sum) is what\'s actually needed.',
        },
        {
          mistake: 'Advancing both pointers unconditionally instead of exactly one',
          why: 'In the converging pattern, moving both left++ and right-- on every iteration regardless of the comparison skips over valid pairs — the whole point is that the comparison result decides which single pointer should move.',
          fix: 'Always branch on the comparison first (sum vs target, or char equality) and move only the pointer that the branch calls for.',
        },
        {
          mistake: 'Off-by-one on the loop condition (< vs <=)',
          why: 'Using left <= right in a converging loop lets left and right end up pointing at the same index and get compared to itself, or lets right go one step below left, producing double-counted or nonsensical pairs.',
          fix: 'Use left < right for converging patterns, so the loop naturally stops the moment the pointers meet or cross — never process an index against itself.',
        },
        {
          mistake: 'Forgetting to recheck a value after a same-direction swap',
          why: 'In partition-style problems (like sorting 0s/1s/2s), swapping a value into the current position from the far end can bring in a value that itself needs processing — advancing the middle pointer immediately skips checking it.',
          fix: 'When a swap brings a new, unexamined value into the current position, don\'t advance that pointer yet — recheck the same index on the next iteration.',
        },
      ],
    },
    {
      id: 'complexity',
      title: 'Time Complexity',
      type: 'complexity',
      rows: [
        { op: 'Opposite-direction convergence (reverse, palindrome, sorted pair-sum)', time: 'O(n)', note: 'Each step moves one pointer strictly inward; the pointers can move at most n times combined before meeting.' },
        { op: 'Same-direction read/write (remove duplicates, partition)', time: 'O(n)', note: 'fast visits every index exactly once; slow never moves faster than fast.' },
        { op: 'Fast & slow (midpoint / cycle detection)', time: 'O(n)', note: 'fast reaches the end (or laps slow on a cycle) in at most n steps.' },
        { op: 'Extra space, in every variant above', time: 'O(1)', note: 'The defining benefit — no second array, list, or set is ever allocated.' },
      ],
    },
    {
      id: 'when',
      title: 'When to Use',
      type: 'text',
      heading: 'Reach for two pointers when you see…',
      body: [
        'A sorted array and a question about a pair or triplet summing to a target.',
        'A request to reverse, or check symmetry (palindrome) in, an array or string.',
        'A request to remove or partition elements in place, without allocating a new array.',
        'A need to find a midpoint, or detect a cycle, in a structure where the length isn\'t known upfront (this is the deeper reason fast/slow exists — it matters most on linked lists).',
        'Avoid it when the data has no usable order and the question is about arbitrary pairs — that\'s a job for hashing (a value → index map), not for pointers that rely on monotonic movement.',
      ],
    },
    {
      id: 'problems',
      title: 'Beginner Problems (Easy → Medium → Advanced)',
      type: 'problems',
      groups: [
        {
          level: 'Easy',
          items: [
            drill('reverse-array-two-pointers'),
            drill('check-palindrome-two-pointers'),
            drill('pair-sum-sorted'),
          ],
        },
        {
          level: 'Medium',
          items: [
            drill('segregate-even-odd'),
            drill('remove-duplicates-sorted'),
            drill('find-middle-fast-slow'),
          ],
        },
        {
          level: 'Advanced',
          items: [
            drill('three-sum-drill'),
            drill('sort-colors'),
            drill('squares-sorted-array'),
          ],
        },
      ],
      note: 'These are foundation drills to build comfort with all three pointer-movement rules before moving into the curated interview problem list for this pattern.',
    },
    {
      id: 'cheatsheet',
      title: 'Cheatsheet & Recap',
      type: 'cheatsheet',
      items: [
        'Opposite-direction (converging): left=0, right=length-1, move exactly one pointer per iteration based on a comparison. Needs sorted or symmetric data.',
        'Same-direction (read/write): slow marks the write position, fast scans ahead — compacts an array in place with zero extra memory.',
        'Fast & slow (different speeds): fast moves twice per step of slow — finds a midpoint, or (on a linked list) detects a cycle, without knowing the length upfront.',
        'The universal payoff: O(n) time, O(1) space — trading a nested loop or an extra data structure for one coordinated pass.',
        'Loop condition is left < right for converging patterns, never <=, so pointers never compare an index to itself.',
        'A converging swap or partition swap sometimes brings in a value that hasn\'t been checked yet — don\'t always advance the pointer after a swap.',
        'If the data isn\'t sorted and there\'s no symmetry to exploit, two pointers usually isn\'t the right tool — reach for a hash map instead.',
      ],
    },
    {
      id: 'playground',
      title: 'Interactive Practice & Quiz',
      type: 'playground',
    },
  ],
};

export default twoPointers;
